import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styles from './NotFound.module.css';

const PingPongBall: React.FC = () => (
    <svg
        viewBox="0 0 120 128"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={styles.ballSvg}
        aria-hidden="true"
    >
        <defs>
            <radialGradient id="ng404ball" cx="36%" cy="30%" r="65%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="50%" stopColor="#f0f4f7" />
                <stop offset="100%" stopColor="#b8ccd8" />
            </radialGradient>
            <radialGradient id="ng404gloss" cx="40%" cy="35%" r="50%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </radialGradient>
            <filter id="ng404shadow" x="-30%" y="-30%" width="160%" height="170%">
                <feDropShadow dx="2" dy="6" stdDeviation="6" floodColor="rgba(89,151,197,0.4)" />
            </filter>
        </defs>

        {/* Ombre au sol */}
        <ellipse cx="60" cy="118" rx="32" ry="6" fill="rgba(0,0,0,0.10)" />

        {/* Corps de la balle */}
        <circle cx="60" cy="60" r="50" fill="url(#ng404ball)" filter="url(#ng404shadow)" />

        {/* Couture — S-curve caractéristique d'une balle de ping-pong */}
        <path
            d="M 12 56 C 28 28, 52 38, 60 52 C 68 66, 92 76, 108 48"
            stroke="#5997c5"
            strokeWidth="2.2"
            strokeLinecap="round"
            fill="none"
            opacity="0.75"
        />

        {/* Reflet spéculaire */}
        <ellipse cx="42" cy="36" rx="16" ry="11" fill="url(#ng404gloss)" transform="rotate(-25 42 36)" />

        {/* 3 étoiles — notation qualité tournoi (★★★) */}
        <g fill="#c8900a" opacity="0.55">
            {[44, 60, 76].map((cx) => (
                <path
                    key={cx}
                    transform={`translate(${cx}, 76)`}
                    d="M0,-4 L1.00,-1.38 L3.80,-1.24 L1.62,0.53 L2.35,3.24 L0,1.70 L-2.35,3.24 L-1.62,0.53 L-3.80,-1.24 L-1.00,-1.38 Z"
                />
            ))}
        </g>
    </svg>
);

type NotFoundContext = 'registration' | 'survey';

interface NotFoundProps {
    context?: NotFoundContext;
}

const NotFound: React.FC<NotFoundProps> = ({ context }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const descriptionKey = context
        ? (`notFound.description.${context}` as const)
        : ('notFound.description.default' as const);

    return (
        <div className={styles.page}>
            <div className={styles.card}>
                <div className={styles.iconWrap}>
                    <PingPongBall />
                </div>

                <p className={styles.code}>404</p>
                <hr className={styles.divider} />

                <h1 className={styles.title}>{t('notFound.title')}</h1>
                <p className={styles.description}>{t(descriptionKey)}</p>

                {!context && (
                    <div className={styles.actions}>
                        <button className={styles.backBtn} onClick={() => navigate(-1)}>
                            <ArrowLeft size={15} />
                            {t('notFound.back')}
                        </button>
                        <button className={styles.homeBtn} onClick={() => navigate('/')}>
                            <Home size={15} />
                            {t('notFound.home')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotFound;
